import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { captureAnalytics, startSessionReplay, stopSessionReplay } from './client';
import type { AnalyticsProperties } from './types';

interface SurfaceContextValue {
  /** The surface's registry name — matches the backend's AnalyticsSurface. */
  surface: string;
  /** Records an event with the surface's properties merged in. */
  track: (event: string, properties?: AnalyticsProperties) => void;
  /** Milliseconds since the surface mounted — the basis of every time-to-X. */
  msSinceStart: () => number;
  /**
   * Remembers how far the visitor got. Read back by the abandonment event, so
   * "where do they drop off" is answered by one property instead of by
   * reconstructing each session from its event trail.
   */
  markStage: (stage: string) => void;
  furthestStage: () => string;
}

const SurfaceContext = createContext<SurfaceContextValue | null>(null);

interface AnalyticsSurfaceProps {
  /** Must match a case name of the backend's AnalyticsSurface enum. */
  name: string;
  /**
   * Properties describing this visit, merged into every event of the surface.
   * Re-read on each track() call, so values that arrive late (an event name
   * that needed a fetch) still reach the events fired after they land.
   */
  properties?: AnalyticsProperties;
  /**
   * The surface's stages in order. markStage() only ever moves forward
   * through this list, so scrolling back or revisiting a step cannot make a
   * session look less complete than it got.
   */
  stages?: readonly string[];
  /**
   * Record session replay while this surface is mounted. On by default: replay
   * is how you see WHY a funnel step leaks, and it is only worth its cost on
   * the surfaces being studied — which is exactly what a surface marks out.
   * Pass false for a high-traffic surface you only want counted.
   */
  record?: boolean;
  children: React.ReactNode;
}

/**
 * Scopes a section of the app as one measured surface: one event namespace,
 * one shared property set, one clock, one notion of progress.
 *
 * Instrumenting a NEW section of the product means wrapping it in this, adding
 * its case to the backend's AnalyticsSurface enum, and declaring its event
 * names — no changes to this package.
 */
export const AnalyticsSurface: React.FC<AnalyticsSurfaceProps> = ({
  name,
  properties,
  stages,
  record = true,
  children,
}) => {
  const startedAtRef = useRef(Date.now());
  const propertiesRef = useRef<AnalyticsProperties | undefined>(properties);
  const stageRef = useRef<string>('');

  propertiesRef.current = properties;

  const msSinceStart = useCallback(() => Date.now() - startedAtRef.current, []);

  const track = useCallback(
    (event: string, eventProperties: AnalyticsProperties = {}) => {
      captureAnalytics(event, {
        surface: name,
        emitter: 'browser',
        ms_since_surface_start: Date.now() - startedAtRef.current,
        ...propertiesRef.current,
        ...eventProperties,
      });
    },
    [name],
  );

  const markStage = useCallback(
    (stage: string) => {
      if (!stages) {
        stageRef.current = stage;

        return;
      }

      const next = stages.indexOf(stage);
      const current = stages.indexOf(stageRef.current);

      if (next > current) {
        stageRef.current = stage;
      }
    },
    [stages],
  );

  const furthestStage = useCallback(() => stageRef.current, []);

  // recording follows the surface's lifetime, so it never leaks into the rest
  // of the product after the visitor navigates away
  useEffect(() => {
    if (!record) {
      return undefined;
    }

    startSessionReplay();

    return () => stopSessionReplay();
  }, [record]);

  const value = useMemo<SurfaceContextValue>(
    () => ({ surface: name, track, msSinceStart, markStage, furthestStage }),
    [name, track, msSinceStart, markStage, furthestStage],
  );

  return <SurfaceContext.Provider value={value}>{children}</SurfaceContext.Provider>;
};

/**
 * The surface a component is being measured inside. Throws nothing outside a
 * surface — telemetry must never be able to break a page — the calls simply
 * become no-ops, which is also what makes instrumented components reusable in
 * uninstrumented places.
 */
export const useSurface = (): SurfaceContextValue => {
  const context = useContext(SurfaceContext);

  return (
    context ?? {
      surface: '',
      track: () => undefined,
      msSinceStart: () => 0,
      markStage: () => undefined,
      furthestStage: () => '',
    }
  );
};
